import { Table } from "@jutro/ui";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDebounce } from "use-debounce";
import { SetContainerRef } from "components/AnimatedHeaderOnScrollWrapper/types";
import {
  EdmMedicalAppShiftWithoutVisitFragment,
  EdmMedicalPerson,
} from "lib/graphql/megaSchema";
import { ActionsCell } from "views/StaffSchedule/AttendanceTracker/components/ActionsCell";
import { SelectedShiftRowsHeader } from "views/StaffSchedule/AttendanceTracker/components/SelectedShiftRowsHeader";
import { StaffCell } from "views/StaffSchedule/AttendanceTracker/components/StaffCell";
import { TimeTagCell } from "views/StaffSchedule/AttendanceTracker/components/TimeTagCell";
import { VisitTypesCell } from "views/StaffSchedule/AttendanceTracker/components/VisitTypesCell";
import {
  filterShiftsBySearchValue,
  filterShiftsByStaffGenre,
  shiftDateFilter,
  sortShifts,
  transformShiftsDay,
} from "views/StaffSchedule/AttendanceTracker/tools";
import { EmptyDoctorsList } from "views/StaffSchedule/components/EmptyDoctorsList";
import { StaffGenreFilter } from "views/StaffSchedule/tools";

const columns: ColumnDef<EdmMedicalAppShiftWithoutVisitFragment>[] = [
  {
    accessorKey: "day",
    header: "Dzień",
    cell: ({ getValue }) => (
      <span className="font-label min-w-64">
        {dayjs(getValue<string>()).format("D MMM (dddd)")}
      </span>
    ),
  },
  {
    accessorKey: "staff",
    header: "Personel",
    cell: ({ getValue }) => <StaffCell staff={getValue<EdmMedicalPerson>()} />,
  },
  {
    id: "visitTypes",
    header: "Typ wizyty",
    cell: ({ row }) => <VisitTypesCell visitTypes={row.original.visitTypes} />,
  },
  {
    accessorKey: "startFrom",
    header: "Start",
    cell: ({ getValue, row }) => (
      <TimeTagCell
        time={getValue<string>()}
        isConfirmedShift={row.original.confirmed}
      />
    ),
  },

  {
    accessorKey: "endedAt",
    header: "Stop",
    cell: ({ getValue, row }) => (
      <TimeTagCell
        time={getValue<string>()}
        isConfirmedShift={row.original.confirmed}
      />
    ),
  },

  {
    id: "actions",
    header: () => <div className="min-w-64" />,
    cell: ({ row }) => <ActionsCell shift={row.original} />,
  },
];

type Props = {
  shifts: EdmMedicalAppShiftWithoutVisitFragment[];
  selectedStaffGenre: StaffGenreFilter;
  searchInputValue: string;
  setSearchInputValue: Dispatch<SetStateAction<string>>;
  setContainerRef: SetContainerRef;
};

export const AttendanceTable = ({
  shifts,
  selectedStaffGenre,
  searchInputValue: _searchInputValue,
  setSearchInputValue,
  setContainerRef,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const [selectedShiftRows, setSelectedShiftRows] = useState<
    EdmMedicalAppShiftWithoutVisitFragment[]
  >([]);

  const [searchInputValue] = useDebounce(_searchInputValue, 500);

  const transformedShifts = useMemo(
    () =>
      shifts.filter(shiftDateFilter).map(transformShiftsDay).sort(sortShifts),
    [shifts],
  );

  const filteredShifts = useMemo(() => {
    const filteredShiftsByMedicalStaffGenre = filterShiftsByStaffGenre(
      transformedShifts,
      selectedStaffGenre,
    );

    return filteredShiftsByMedicalStaffGenre.filter((shift) =>
      filterShiftsBySearchValue(shift, searchInputValue),
    );
  }, [transformedShifts, searchInputValue, selectedStaffGenre]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    setContainerRef(ref);
  }, [ref]);

  if (filteredShifts.length === 0) {
    return (
      <EmptyDoctorsList
        searchInputValue={searchInputValue}
        onSearchInputValueReset={() => setSearchInputValue("")}
        mode="attendanceTracker"
      />
    );
  }

  return (
    <div className="overflow-x-scroll">
      <Table
        ref={ref}
        columns={columns}
        data={filteredShifts}
        groupBy="day"
        selectionRowMode="multiple"
        selectedRows={selectedShiftRows}
        setSelectedRows={setSelectedShiftRows}
        selectedRowsHeader={
          <SelectedShiftRowsHeader
            selectedShiftRows={selectedShiftRows}
            setSelectedShiftRows={setSelectedShiftRows}
          />
        }
      />
    </div>
  );
};
